<template>
  <div class="mini-tab-content">
    <div class="interc-toolbar">
      <span class="interc-toolbar__title">
        <feather-icon
          icon="MapPinIcon"
          size="13"
          class="mr-25 text-success"
        />
        {{ activeDestination ? $t('involvedPanel.destination.toolbar.statusDefined') : $t('involvedPanel.destination.toolbar.statusNone') }}
      </span>
      <button
        v-if="!activeDestination && $hasAbility('regulacao.gerenciar')"
        class="btn btn-sm btn-success"
        @click="openDestinationModal"
      >
        <feather-icon
          icon="PlusIcon"
          size="13"
          class="mr-25"
        />
        {{ $t('involvedPanel.destination.toolbar.newButton') }}
      </button>
    </div>

    <div
      v-if="!involvedDestinations.length"
      class="interc-empty"
    >
      <feather-icon
        icon="MapPinIcon"
        size="26"
      />
      <p>{{ $t('involvedPanel.destination.empty.noDestinations') }}</p>
    </div>

    <div
      v-if="involvedDestinations.length"
      class="interc-list"
    >
      <div
        v-for="dest in involvedDestinations"
        :key="dest.id"
        :class="['outcome-item', { 'outcome-item--cancelled': dest.cancelled_at }]"
      >
        <div class="interc-item__top">
          <div class="outcome-item__badge">
            <feather-icon
              :icon="dest.cancelled_at ? 'XCircleIcon' : 'MapPinIcon'"
              size="11"
              class="mr-25"
            />
            {{ dest.establishment ? dest.establishment.establishment_name : '—' }}
          </div>
          <button
            v-if="!dest.cancelled_at && $hasAbility('regulacao.gerenciar')"
            class="interc-item__del"
            :title="$t('involvedPanel.destination.button.cancelTooltip')"
            @click="cancelDestination(dest.id)"
          >
            <feather-icon
              icon="Trash2Icon"
              size="13"
            />
          </button>
        </div>
        <p
          v-if="dest.establishment"
          class="interc-item__desc"
        >
          <feather-icon
            icon="NavigationIcon"
            size="11"
            class="mr-25"
          />
          {{ formatAddress(dest.establishment) }}
        </p>
        <p
          v-if="dest.observation"
          class="interc-item__desc"
        >
          {{ dest.observation }}
        </p>
        <div class="interc-item__meta">
          <feather-icon
            icon="UserIcon"
            size="11"
          />
          <span>{{ dest.registered_by ? dest.registered_by.name : '—' }}</span>
          <span class="interc-item__dot" />
          <feather-icon
            icon="ClockIcon"
            size="11"
          />
          <span>{{ dest.created_at | formatDate }}</span>
        </div>
        <div
          v-if="dest.cancelled_at"
          class="interc-item__cancelled-row"
        >
          <feather-icon
            icon="XCircleIcon"
            size="11"
            class="mr-25"
          />
          {{ $t('involvedPanel.destination.cancelled.cancelledByLabel') }}
          <strong>{{ dest.cancelled_by ? dest.cancelled_by.name : '—' }}</strong>
          · {{ dest.cancelled_at | formatDate }}
        </div>
      </div>
    </div>

    <DefineDestinationModal
      v-model="showDestinationModal"
      :occurrence-involved-id="occurrenceInvolvedId"
      :origin-lat="occurrenceLatitude"
      :origin-lng="occurrenceLongitude"
      :occurrence-dispatch-type-id="occurrenceDispatchTypeId"
      :occurrence-dispatch-reason-id="occurrenceDispatchReasonId"
      @saved="onDestinationSaved"
    />
  </div>
</template>

<script>
import involvedPanelMixin from './involvedPanelMixin'
import DefineDestinationModal from './DefineDestinationModal.vue'

export default {
  name: 'DestinationPanel',
  components: { DefineDestinationModal },
  mixins: [involvedPanelMixin],
  props: {
    initialDestinations: { type: Array, default: () => [] },
    occurrenceLatitude: { type: [Number, String], default: null },
    occurrenceLongitude: { type: [Number, String], default: null },
    occurrenceDispatchTypeId: { type: [Number, String], default: null },
    occurrenceDispatchReasonId: { type: [Number, String], default: null },
  },
  data() {
    return {
      involvedDestinations: [...(this.initialDestinations || [])],
      showDestinationModal: false,
    }
  },
  computed: {
    activeDestination() {
      return this.involvedDestinations.find(d => !d.cancelled_at) || null
    },
  },
  methods: {
    formatAddress(establishment) {
      const parts = [];
      let street = establishment.address || '';
      if (establishment.number) street += `, ${establishment.number}`;
      if (street) parts.push(street);
      if (establishment.complement) parts.push(establishment.complement);
      if (establishment.neighborhood) parts.push(establishment.neighborhood);

      const city = establishment.city ? establishment.city.name : '';
      const state = establishment.state ? establishment.state.code : '';
      const cityState = [city, state].filter(Boolean).join('/');
      if (cityState) parts.push(cityState);

      if (establishment.zip_code) parts.push(`CEP ${establishment.zip_code}`);

      return parts.join(' - ') || '—';
    },
    openDestinationModal() {
      this.showDestinationModal = true
    },
    onDestinationSaved(saved) {
      this.involvedDestinations.push(saved)
      this.$emit('destination-saved', this.involvedDestinations)
    },
    async cancelDestination(id) {
      const confirmed = await this.$bvModal.msgBoxConfirm(
        this.$t('involvedPanel.destination.confirm.message'),
        {
          title: this.$t('involvedPanel.destination.confirm.title'), okTitle: this.$t('involvedPanel.destination.confirm.okButton'), cancelTitle: this.$t('involvedPanel.destination.confirm.cancelButton'), okVariant: 'danger', centered: true, size: 'sm',
        },
      )
      if (!confirmed) return
      try {
        const res = await this.$http.delete(`${this.$store.state.api}occurrence_involved_destinations/${id}`)
        const updated = res.data.data || res.data
        const idx = this.involvedDestinations.findIndex(d => d.id === id)
        if (idx !== -1) this.$set(this.involvedDestinations, idx, updated)
        this.$emit('destination-saved', this.involvedDestinations)
      } catch (e) {
        this.$message(this.$t('involvedPanel.destination.error.title'), this.$t('involvedPanel.destination.error.cancelFailed'), 'error')
      }
    },
  },
}
</script>
